import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatRomeDate, formatRomeDateTime } from '../lib/datetime';
import { api, useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import PhotoUpload from '../components/PhotoUpload';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  ArrowLeft,
  Building2,
  MapPin,
  Clock,
  Tag,
  AlertTriangle,
  Loader2,
  Send,
  CheckCircle2,
  Wrench,
  Package,
  Camera,
  Pencil,
  Save,
  X,
  Trash2,
  ShieldAlert,
  ShieldOff,
  Eye
} from 'lucide-react';

const warningLevelLabels = {
  lieve: 'Lieve',
  medio: 'Medio',
  grave: 'Grave',
};

const warningLevelStyles = {
  lieve: { bg: 'rgba(250, 204, 21, 0.16)', border: '#facc15', text: '#a16207', icon: '#ca8a04' },
  medio: { bg: 'rgba(245, 158, 11, 0.16)', border: '#f59e0b', text: '#b45309', icon: '#d97706' },
  grave: { bg: 'rgba(220, 38, 38, 0.16)', border: '#dc2626', text: '#991b1b', icon: '#dc2626' },
};

const priorityLabels = {
  urgente: 'Urgente',
  alta: 'Alta',
  media: 'Media',
  bassa: 'Bassa'
};

const statusLabels = {
  aperto: 'Aperto',
  assegnato: 'Assegnato',
  in_lavorazione: 'In Lavorazione',
  in_attesa_pezzi: 'In Attesa Pezzi',
  completato: 'Completato',
  chiuso: 'Chiuso'
};

const TicketDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [hotel, setHotel] = useState(null);
  const [category, setCategory] = useState(null);
  const [technicians, setTechnicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [technicalNote, setTechnicalNote] = useState('');

  // Edit mode state for admin detail editing
  const [editMode, setEditMode] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [categories, setCategories] = useState([]);
  const [hotelAreas, setHotelAreas] = useState({ rooms: [], common_areas: [], all_areas: [] });
  const [editData, setEditData] = useState({ hotel_id: '', area: '', category_id: '', priority: '' });

  // Warning card (Cartellino di ammonizione) state
  const [warnings, setWarnings] = useState([]);
  const [warningDialogOpen, setWarningDialogOpen] = useState(false);
  const [warningLevel, setWarningLevel] = useState('lieve');
  const [warningReason, setWarningReason] = useState('');
  const [warningSubmitting, setWarningSubmitting] = useState(false);
  const [warningSummary, setWarningSummary] = useState({ monthly_active_count: 0, under_observation: false });

  const canAssign = ['super_admin', 'hotel_manager', 'capo_manutentore'].includes(user?.role);
  const canIssueWarning = ['super_admin', 'hotel_manager'].includes(user?.role);
  const canRevokeWarning = user?.role === 'super_admin';

  const fetchTicket = async () => {
    try {
      setLoading(true);
      const { data: ticketData } = await api.get(`/tickets/${id}`);
      setTicket(ticketData);

      // Fetch hotels and categories always (needed for display and editing)
      const [hotelsRes, catsRes] = await Promise.all([
        api.get('/hotels'),
        api.get('/categories')
      ]);
      setHotels(hotelsRes.data);
      setCategories(catsRes.data);

      // Resolve hotel and category for display
      const h = hotelsRes.data.find(h => h.id === ticketData.hotel_id);
      setHotel(h || null);

      const cat = catsRes.data.find(c => c.id === ticketData.category_id);
      setCategory(cat || null);

      // Fetch technicians if admin/manager (wrapped in try-catch to handle permission errors)
      if (['super_admin', 'hotel_manager', 'capo_manutentore'].includes(user?.role)) {
        try {
          const { data: users } = await api.get('/users');
          setTechnicians(users.filter(u => u.role === 'technician'));
        } catch (userErr) {
          // Hotel manager may not have permission to fetch users - this is OK
          console.log('Could not fetch technicians list');
        }
      }

      // Fetch warnings for this ticket (visible to all authenticated users)
      try {
        const { data: wList } = await api.get(`/tickets/${id}/warnings`);
        setWarnings(wList || []);
      } catch (wErr) {
        setWarnings([]);
      }

      // Fetch monthly warning summary for the assigned tech (only for managers/admins)
      if (ticketData.assigned_to && ['super_admin', 'hotel_manager', 'capo_manutentore'].includes(user?.role)) {
        try {
          const { data: techW } = await api.get(`/users/${ticketData.assigned_to}/warnings`);
          setWarningSummary({
            monthly_active_count: techW.monthly_active_count || 0,
            under_observation: techW.under_observation || false,
          });
        } catch (e) {
          setWarningSummary({ monthly_active_count: 0, under_observation: false });
        }
      } else {
        setWarningSummary({ monthly_active_count: 0, under_observation: false });
      }
    } catch (err) {
      toast.error('Errore nel caricamento del ticket');
      navigate('/tickets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  const fetchHotelAreas = async (hotelId) => {
    if (!hotelId) {
      setHotelAreas({ rooms: [], common_areas: [], all_areas: [] });
      return;
    }
    try {
      const { data } = await api.get(`/hotels/${hotelId}/areas`);
      setHotelAreas(data);
    } catch {
      setHotelAreas({ rooms: [], common_areas: [], all_areas: [] });
    }
  };

  const startEdit = async () => {
    setEditData({
      hotel_id: ticket.hotel_id || '',
      area: ticket.area || '',
      category_id: ticket.category_id || '',
      priority: ticket.priority || 'media'
    });
    if (ticket.hotel_id) {
      await fetchHotelAreas(ticket.hotel_id);
    }
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
  };

  const saveEdit = async () => {
    const updates = {};
    if (editData.hotel_id !== (ticket.hotel_id || '')) updates.hotel_id = editData.hotel_id;
    if (editData.area !== (ticket.area || '')) updates.area = editData.area;
    if (editData.category_id !== (ticket.category_id || '')) updates.category_id = editData.category_id;
    if (editData.priority !== (ticket.priority || 'media')) updates.priority = editData.priority;

    if (Object.keys(updates).length === 0) {
      setEditMode(false);
      return;
    }
    await updateTicket(updates);
    setEditMode(false);
  };

  const updateTicket = async (updates) => {
    try {
      setUpdating(true);
      await api.put(`/tickets/${id}`, updates);
      toast.success('Ticket aggiornato');
      fetchTicket();
    } catch (err) {
      toast.error("Errore nell'aggiornamento");
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = (newStatus) => {
    updateTicket({ status: newStatus });
  };

  const handleAssign = (techId) => {
    updateTicket({ assigned_to: techId });
  };

  const handleAddNote = () => {
    if (!technicalNote.trim()) return;
    updateTicket({ technical_notes: technicalNote });
    setTechnicalNote('');
  };

  const canDelete = ['super_admin', 'hotel_manager', 'capo_manutentore'].includes(user?.role);

  const handleDeleteTicket = async () => {
    try {
      await api.delete(`/tickets/${id}`);
      toast.success('Ticket eliminato con successo');
      navigate('/tickets');
    } catch (err) {
      toast.error("Errore nell'eliminazione del ticket");
    }
  };

  const openWarningDialog = () => {
    setWarningLevel('lieve');
    setWarningReason('');
    setWarningDialogOpen(true);
  };

  const submitWarning = async () => {
    if (!warningReason.trim() || warningReason.trim().length < 5) {
      toast.error('Inserisci un motivo (minimo 5 caratteri)');
      return;
    }
    try {
      setWarningSubmitting(true);
      const { data } = await api.post(`/tickets/${id}/warning`, {
        level: warningLevel,
        reason: warningReason.trim(),
      });
      toast.success('Cartellino emesso');
      if (data.under_observation) {
        toast.warning(`Tecnico ora SOTTO OSSERVAZIONE (${data.monthly_active_count} cartellini nel mese)`);
      }
      setWarningDialogOpen(false);
      await fetchTicket();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore nell'emissione del cartellino";
      toast.error(msg);
    } finally {
      setWarningSubmitting(false);
    }
  };

  const revokeWarning = async (warningId) => {
    if (!window.confirm('Annullare definitivamente questo cartellino?')) return;
    try {
      await api.delete(`/tickets/${id}/warning/${warningId}`, { data: { revoke_reason: '' } });
      toast.success('Cartellino annullato');
      await fetchTicket();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Errore nell'annullamento";
      toast.error(msg);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-900" />
      </div>
    );
  }

  if (!ticket) return null;

  const canChangeStatus = user?.role === 'technician' || canAssign;

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-button">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-black text-slate-900">{ticket.title}</h1>
          <p className="text-slate-500">Ticket #{ticket.id?.slice(-8)}</p>
        </div>
        {canDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm" data-testid="delete-ticket-button">
                <Trash2 className="w-4 h-4 mr-2" />
                Elimina
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Eliminare questo ticket?</AlertDialogTitle>
                <AlertDialogDescription>
                  Stai per eliminare il ticket <strong>"{ticket.title}"</strong>. Questa azione è irreversibile e tutti i dati associati verranno persi.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-delete-button">Annulla</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDeleteTicket}
                  className="bg-red-600 hover:bg-red-700"
                  data-testid="confirm-delete-button"
                >
                  Elimina definitivamente
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Ticket info */}
          <Card className="border-slate-200">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-bold">Dettagli</CardTitle>
                <div className="flex items-center gap-2">
                  <Badge className={`priority-${ticket.priority}`}>
                    {priorityLabels[ticket.priority] || ticket.priority}
                  </Badge>
                  <Badge variant="secondary" className={`status-${ticket.status}`}>
                    {statusLabels[ticket.status] || ticket.status}
                  </Badge>
                  {canAssign && !editMode && (
                    <Button variant="ghost" size="icon" onClick={startEdit} data-testid="edit-details-button" className="ml-2 h-8 w-8">
                      <Pencil className="w-4 h-4 text-slate-500" />
                    </Button>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-slate-700">{ticket.description}</p>

              {!editMode ? (
                <div className="grid grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  <div className="flex items-center gap-2 text-sm">
                    <Building2 className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{hotel?.name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{ticket.area || 'N/A'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">{category?.name || 'Non categorizzato'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-600">
                      {formatRomeDateTime(ticket.created_at)}
                    </span>
                  </div>
                </div>
              ) : (
                /* Edit mode form */
                <div className="pt-4 border-t border-slate-100 space-y-4" data-testid="edit-details-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-500">Hotel</label>
                      <Select
                        value={editData.hotel_id || '_none'}
                        onValueChange={(v) => {
                          const val = v === '_none' ? '' : v;
                          setEditData({ ...editData, hotel_id: val, area: '' });
                          fetchHotelAreas(val);
                        }}
                      >
                        <SelectTrigger data-testid="edit-hotel-select">
                          <SelectValue placeholder="Seleziona hotel" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">-- Nessuno --</SelectItem>
                          {hotels.map(h => (
                            <SelectItem key={h.id} value={h.id}>{h.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-500">Area/Camera</label>
                      {hotelAreas.all_areas.length > 0 ? (
                        <Select
                          value={editData.area || '_none'}
                          onValueChange={(v) => setEditData({ ...editData, area: v === '_none' ? '' : v })}
                        >
                          <SelectTrigger data-testid="edit-area-select">
                            <SelectValue placeholder="Seleziona area" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="_none">-- Nessuna --</SelectItem>
                            {hotelAreas.common_areas.length > 0 && (
                              <>
                                <div className="px-2 py-1.5 text-[10px] font-bold uppercase text-slate-400">Aree Comuni</div>
                                {hotelAreas.common_areas.map(a => (
                                  <SelectItem key={`area-${a}`} value={a}>{a}</SelectItem>
                                ))}
                                <div className="px-2 py-1.5 text-[10px] font-bold uppercase text-slate-400 mt-1">Camere</div>
                              </>
                            )}
                            {hotelAreas.rooms.map(r => (
                              <SelectItem key={`room-${r}`} value={r}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <input
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                          value={editData.area}
                          onChange={(e) => setEditData({ ...editData, area: e.target.value })}
                          placeholder="es. 301, Hall"
                          data-testid="edit-area-input"
                        />
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-500">Categoria</label>
                      <Select
                        value={editData.category_id || '_none'}
                        onValueChange={(v) => setEditData({ ...editData, category_id: v === '_none' ? '' : v })}
                      >
                        <SelectTrigger data-testid="edit-category-select">
                          <SelectValue placeholder="Seleziona categoria" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none">-- Nessuna --</SelectItem>
                          {categories.map(c => (
                            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <label className="text-xs font-medium text-slate-500">Priorita</label>
                      <Select
                        value={editData.priority}
                        onValueChange={(v) => setEditData({ ...editData, priority: v })}
                      >
                        <SelectTrigger data-testid="edit-priority-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="urgente">Urgente</SelectItem>
                          <SelectItem value="alta">Alta</SelectItem>
                          <SelectItem value="media">Media</SelectItem>
                          <SelectItem value="bassa">Bassa</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="cancel-edit-button">
                      <X className="w-4 h-4 mr-1" /> Annulla
                    </Button>
                    <Button size="sm" className="bg-blue-900 hover:bg-blue-800" onClick={saveEdit} disabled={updating} data-testid="save-edit-button">
                      {updating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                      Salva
                    </Button>
                  </div>
                </div>
              )}

              {ticket.is_room_occupied && (
                <div className="flex items-center gap-2 p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Camera occupata - Priorita elevata
                </div>
              )}
            </CardContent>
          </Card>

          {/* Photos */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-bold flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Foto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <PhotoUpload
                ticketId={ticket.id}
                photos={ticket.photos || []}
                onPhotoAdded={(url) => {
                  setTicket(prev => ({
                    ...prev,
                    photos: [...(prev.photos || []), url]
                  }));
                }}
              />
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-bold">Cronologia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(ticket.timeline || []).map((event, index) => (
                  <div key={`${event.timestamp}-${event.action}-${index}`} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        event.action === 'completato' ? 'bg-green-100 text-green-600' :
                        event.action === 'assegnato' ? 'bg-purple-100 text-purple-600' :
                        event.action === 'in_lavorazione' ? 'bg-cyan-100 text-cyan-600' :
                        event.action === 'modificato' ? 'bg-amber-100 text-amber-600' :
                        event.action === 'ammonizione' ? 'bg-red-100 text-red-600' :
                        event.action === 'ammonizione_annullata' ? 'bg-slate-200 text-slate-500' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        {event.action === 'completato' ? <CheckCircle2 className="w-4 h-4" /> :
                         event.action === 'in_lavorazione' ? <Wrench className="w-4 h-4" /> :
                         event.action === 'in_attesa_pezzi' ? <Package className="w-4 h-4" /> :
                         event.action === 'modificato' ? <Pencil className="w-4 h-4" /> :
                         event.action === 'ammonizione' ? <ShieldAlert className="w-4 h-4" /> :
                         event.action === 'ammonizione_annullata' ? <ShieldOff className="w-4 h-4" /> :
                         <Clock className="w-4 h-4" />}
                      </div>
                      {index < (ticket.timeline || []).length - 1 && (
                        <div className="w-px h-full bg-slate-200 mt-2" />
                      )}
                    </div>
                    <div className="flex-1 pb-4">
                      <p className="text-sm font-medium text-slate-900">{event.note}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {formatRomeDateTime(event.timestamp)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Warning Cards (Cartellini di ammonizione) — visible if any exist or if user can issue */}
          {(warnings.length > 0 || (canIssueWarning && ['chiuso', 'completato'].includes(ticket.status))) && (
            <Card className="border-slate-200" data-testid="warning-cards-section">
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <CardTitle className="text-lg font-bold flex items-center gap-2">
                    <ShieldAlert className="w-5 h-5 text-amber-600" />
                    Cartellini di Ammonizione
                    {warnings.filter(w => w.active).length > 0 && (
                      <Badge variant="secondary" className="ml-1" data-testid="warning-active-count-badge">
                        {warnings.filter(w => w.active).length} attivi
                      </Badge>
                    )}
                  </CardTitle>
                  {canIssueWarning && ['chiuso', 'completato'].includes(ticket.status) && ticket.assigned_to && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-amber-500 text-amber-700 hover:bg-amber-50"
                      onClick={openWarningDialog}
                      data-testid="issue-warning-button"
                    >
                      <ShieldAlert className="w-4 h-4 mr-1" />
                      Emetti cartellino
                    </Button>
                  )}
                </div>
                {warningSummary.under_observation && ticket.assigned_to && (
                  <div
                    className="mt-3 flex items-center gap-2 px-3 py-2 rounded-md text-xs font-semibold"
                    style={{ background: 'rgba(220, 38, 38, 0.12)', border: '1px solid rgba(220, 38, 38, 0.4)', color: '#991b1b' }}
                    data-testid="under-observation-banner"
                  >
                    <Eye className="w-4 h-4" />
                    Tecnico SOTTO OSSERVAZIONE — {warningSummary.monthly_active_count} cartellini attivi nel mese corrente
                  </div>
                )}
              </CardHeader>
              <CardContent>
                {warnings.length === 0 ? (
                  <p className="text-sm text-slate-500" data-testid="no-warnings-text">
                    Nessun cartellino emesso su questo ticket.
                  </p>
                ) : (
                  <div className="space-y-3" data-testid="warnings-list">
                    {warnings.map((w) => {
                      const style = warningLevelStyles[w.level] || warningLevelStyles.lieve;
                      const revoked = !w.active;
                      return (
                        <div
                          key={w.id}
                          className="rounded-lg p-4"
                          style={{
                            background: revoked ? 'rgba(148, 163, 184, 0.10)' : style.bg,
                            borderLeft: revoked ? '4px solid #94a3b8' : `4px solid ${style.border}`,
                            opacity: revoked ? 0.7 : 1,
                          }}
                          data-testid={`warning-item-${w.id}`}
                        >
                          <div className="flex items-start justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-2 flex-wrap">
                              <Badge
                                variant="secondary"
                                style={{
                                  background: revoked ? '#e2e8f0' : style.bg,
                                  color: revoked ? '#64748b' : style.text,
                                  border: revoked ? '1px solid #cbd5e1' : `1px solid ${style.border}`,
                                }}
                              >
                                {warningLevelLabels[w.level] || w.level}
                              </Badge>
                              <span className="text-sm font-semibold text-slate-900">
                                Da {w.issued_by_name}
                              </span>
                              {revoked && (
                                <Badge variant="outline" className="text-xs">ANNULLATO</Badge>
                              )}
                            </div>
                            {canRevokeWarning && !revoked && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-7 text-xs text-slate-500 hover:text-red-600"
                                onClick={() => revokeWarning(w.id)}
                                data-testid={`revoke-warning-${w.id}`}
                              >
                                <ShieldOff className="w-3 h-3 mr-1" />
                                Annulla
                              </Button>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 mt-2">{w.reason}</p>
                          <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                            <span>Tecnico: <strong>{w.technician_name}</strong></span>
                            <span>•</span>
                            <span>{formatRomeDateTime(w.created_at)}</span>
                            {revoked && w.revoked_at && (
                              <>
                                <span>•</span>
                                <span>Annullato da {w.revoked_by_name} il {formatRomeDateTime(w.revoked_at)}</span>
                              </>
                            )}
                          </div>
                          {revoked && w.revoke_reason && (
                            <p className="text-xs text-slate-500 mt-1 italic">Motivo annullamento: {w.revoke_reason}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Technical notes */}
          {(user?.role === 'technician' || canAssign) && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold">Note Tecniche</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {ticket.technical_notes && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-700">{ticket.technical_notes}</p>
                  </div>
                )}

                <div className="flex gap-2">
                  <Textarea
                    placeholder="Aggiungi una nota tecnica..."
                    value={technicalNote}
                    onChange={(e) => setTechnicalNote(e.target.value)}
                    className="flex-1"
                    data-testid="technical-note-input"
                  />
                  <Button
                    onClick={handleAddNote}
                    disabled={!technicalNote.trim() || updating}
                    className="bg-blue-900 hover:bg-blue-800"
                    data-testid="add-note-button"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Actions */}
          {canChangeStatus && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold">Azioni</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status change */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">Cambia Stato</label>
                  <Select
                    value={ticket.status}
                    onValueChange={handleStatusChange}
                    disabled={updating}
                  >
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aperto">Aperto</SelectItem>
                      <SelectItem value="assegnato">Assegnato</SelectItem>
                      <SelectItem value="in_lavorazione">In Lavorazione</SelectItem>
                      <SelectItem value="in_attesa_pezzi">In Attesa Pezzi</SelectItem>
                      <SelectItem value="completato">Completato</SelectItem>
                      <SelectItem value="chiuso">Chiuso</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Quick actions for technicians */}
                {user?.role === 'technician' && (
                  <div className="grid grid-cols-2 gap-2 pt-2">
                    <Button
                      variant="outline"
                      className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                      onClick={() => handleStatusChange('in_lavorazione')}
                      disabled={updating || ticket.status === 'in_lavorazione'}
                      data-testid="start-work-button"
                    >
                      <Wrench className="w-4 h-4 mr-1" />
                      Inizia
                    </Button>
                    <Button
                      variant="outline"
                      className="border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => handleStatusChange('completato')}
                      disabled={updating || ticket.status === 'completato'}
                      data-testid="complete-button"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Completa
                    </Button>
                  </div>
                )}

                {/* Assign technician */}
                {canAssign && (
                  <div className="space-y-2 pt-2 border-t border-slate-100">
                    <label className="text-sm font-medium text-slate-700">Assegna Tecnico</label>
                    {ticket.assigned_to_is_ex && (
                      <div
                        className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-2.5 py-2 text-xs text-amber-800 dark:text-amber-200"
                        data-testid="ex-employee-warning"
                      >
                        <ShieldAlert className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span>
                          Attuale assegnatario <b>{ticket.assigned_to_name}</b> è un <b>ex dipendente</b>. Riassegna il ticket a un tecnico attivo.
                        </span>
                      </div>
                    )}
                    <Select
                      value={ticket.assigned_to || '_unassigned'}
                      onValueChange={(v) => { if (v !== '_unassigned') handleAssign(v); }}
                      disabled={updating}
                    >
                      <SelectTrigger data-testid="assign-select">
                        <SelectValue placeholder="Seleziona tecnico" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_unassigned">-- Seleziona tecnico --</SelectItem>
                        {technicians.map(tech => (
                          <SelectItem key={tech.id} value={tech.id}>{tech.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Info card */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg font-bold">Informazioni</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="text-slate-500">Creato</span>
                <span className="text-slate-900">{formatRomeDate(ticket.created_at)}</span>
              </div>
              {(ticket.created_by_name || ticket.created_by_is_ex) && (
                <>
                  <Separator />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-slate-500">Aperto da</span>
                    <span className="text-slate-900 font-medium flex items-center gap-1.5 text-right" data-testid="ticket-detail-creator">
                      <span className={ticket.created_by_is_ex ? 'line-through decoration-slate-400' : ''}>
                        {ticket.created_by_name || 'Utente'}
                      </span>
                      {ticket.created_by_is_ex && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                          title="Ex dipendente"
                          data-testid="creator-ex-badge"
                        >
                          Ex
                        </span>
                      )}
                    </span>
                  </div>
                </>
              )}
              {ticket.assigned_to && (
                <>
                  <Separator />
                  <div className="flex justify-between items-center gap-2">
                    <span className="text-slate-500">Assegnato a</span>
                    <span className="text-slate-900 font-medium flex items-center gap-1.5 text-right" data-testid="ticket-detail-assignee">
                      <span className={ticket.assigned_to_is_ex ? 'line-through decoration-slate-400' : ''}>
                        {ticket.assigned_to_name || 'Tecnico'}
                      </span>
                      {ticket.assigned_to_is_ex && (
                        <span
                          className="px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wider bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200"
                          title="Ex dipendente"
                          data-testid="assignee-ex-badge"
                        >
                          Ex
                        </span>
                      )}
                    </span>
                  </div>
                </>
              )}
              <Separator />
              <div className="flex justify-between">
                <span className="text-slate-500">Aggiornato</span>
                <span className="text-slate-900">{formatRomeDate(ticket.updated_at)}</span>
              </div>
              {ticket.closed_at && (
                <>
                  <Separator />
                  <div className="flex justify-between">
                    <span className="text-slate-500">Chiuso</span>
                    <span className="text-slate-900">{formatRomeDate(ticket.closed_at)}</span>
                  </div>
                </>
              )}
              {ticket.operator_signature && (
                <>
                  <Separator />
                  <div className="flex justify-between" data-testid="ticket-detail-operator-signature">
                    <span className="text-slate-500">Firma operatore</span>
                    <span className="text-slate-900 font-medium">{ticket.operator_signature}</span>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Warning Dialog */}
      <Dialog open={warningDialogOpen} onOpenChange={setWarningDialogOpen}>
        <DialogContent className="max-w-md" data-testid="warning-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-600" />
              Emetti cartellino di ammonizione
            </DialogTitle>
            <DialogDescription>
              Stai per emettere un cartellino a <strong>{ticket.assigned_to_name || 'tecnico assegnato'}</strong> per il ticket <strong>&ldquo;{ticket.title}&rdquo;</strong>. Verra registrato nello storico e notificato al tecnico.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Livello</label>
              <div className="grid grid-cols-3 gap-2">
                {['lieve', 'medio', 'grave'].map((lvl) => {
                  const style = warningLevelStyles[lvl];
                  const selected = warningLevel === lvl;
                  return (
                    <button
                      key={lvl}
                      type="button"
                      onClick={() => setWarningLevel(lvl)}
                      className="px-3 py-2 rounded-md text-sm font-semibold border transition-all"
                      style={{
                        background: selected ? style.bg : 'transparent',
                        borderColor: selected ? style.border : '#cbd5e1',
                        color: selected ? style.text : '#475569',
                      }}
                      data-testid={`warning-level-${lvl}`}
                    >
                      {warningLevelLabels[lvl]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Motivo</label>
              <Textarea
                value={warningReason}
                onChange={(e) => setWarningReason(e.target.value)}
                placeholder="Spiega il motivo del cartellino (min. 5 caratteri)..."
                rows={4}
                data-testid="warning-reason-input"
              />
              <p className="text-xs text-slate-400">{warningReason.length} caratteri</p>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setWarningDialogOpen(false)}
              disabled={warningSubmitting}
              data-testid="cancel-warning-button"
            >
              Annulla
            </Button>
            <Button
              onClick={submitWarning}
              disabled={warningSubmitting || !warningReason.trim() || warningReason.trim().length < 5}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-warning-button"
            >
              {warningSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <ShieldAlert className="w-4 h-4 mr-1" />}
              Emetti cartellino
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TicketDetailPage;
